import React, { Component } from 'react';
import { Button } from '@blueprintjs/core';
import { stripeForAccount } from 'helpers/bookingCardPayment';

// Card field for the public booking forms, using Stripe.js directly (Stripe
// Elements card) on the company's connected Stripe account. Card details go
// straight to Stripe; the page only gets a handle to charge with:
//   onReady({ stripe, card, isComplete(), focus() })  (null when unavailable)
// Styles: .bk-card-payment in styles/booking-public.scss.

const FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const cardStyle = () => {
  const mobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
  return {
    base: {
      color: '#022047',
      fontFamily: FONT_STACK,
      // 16px on phones stops iOS zooming into the field.
      fontSize: mobile ? '16px' : '14px',
      fontSmoothing: 'antialiased',
      '::placeholder': { color: '#6b7a90' }
    },
    invalid: {
      color: '#c23030',
      iconColor: '#c23030'
    }
  };
};

class BookingCardPayment extends Component {
  constructor(props) {
    super(props);
    this.state = { unavailable: false, liveError: null };
    // Plain fields, not observables: Stripe objects must not be proxied.
    this.card = null;
    this.complete = false;
    this.fieldRef = React.createRef();
  }

  componentDidMount() {
    this.mountCard();
  }

  componentDidUpdate(prevProps) {
    if(prevProps.stripeAccount !== this.props.stripeAccount) {
      this.unmountCard();
      this.mountCard();
    } else if(prevProps.disabled !== this.props.disabled && this.card) {
      this.card.update({ disabled: Boolean(this.props.disabled) });
    }
  }

  componentWillUnmount() {
    this.unmountCard();
  }

  notify(api) {
    if(this.props.onReady) this.props.onReady(api);
  }

  mountCard() {
    const stripe = stripeForAccount(this.props.stripeAccount);
    if(!stripe || !this.fieldRef.current) {
      this.setState({ unavailable: true });
      this.notify(null);
      return;
    }
    try {
      const card = stripe.elements().create('card', {
        style: cardStyle(),
        disabled: Boolean(this.props.disabled)
      });
      card.on('change', (event) => {
        this.complete = Boolean(event && event.complete);
        this.setState({ liveError: event && event.error ? event.error.message : null });
        if(this.props.onChange) this.props.onChange({ complete: this.complete, empty: Boolean(event && event.empty) });
      });
      card.mount(this.fieldRef.current);
      this.card = card;
      this.complete = false;
      this.setState({ unavailable: false, liveError: null });
      this.notify({
        stripe,
        card,
        isComplete: () => this.complete,
        focus: () => { if(this.card) this.card.focus(); }
      });
    } catch(error) {
      this.setState({ unavailable: true });
      this.notify(null);
    }
  }

  unmountCard() {
    if(this.card) {
      try { this.card.destroy(); } catch(error) { /* already gone */ }
      this.card = null;
    }
    this.complete = false;
    this.notify(null);
  }

  render() {
    const { disabled, busy, error, amountLabel, onRetry, retrying, charged } = this.props;
    const { unavailable, liveError } = this.state;
    const message = unavailable ? 'Card payments are unavailable right now. Please choose another payment method or try again later.' : (error || liveError);

    return (
      <div className={`bk-card-payment${disabled || busy ? ' is-disabled' : ''}`} aria-busy={busy ? 'true' : undefined}>
        <span className="bk-label" id="bk-card-payment-label">Card details</span>
        <div
          className={`bk-card-payment__field${message ? ' is-error' : ''}`}
          ref={this.fieldRef}
          aria-labelledby="bk-card-payment-label"
        />
        {message &&
          <p className="bk-card-payment__error" role="alert">{message}</p>
        }
        {onRetry ?
          <div className="bk-card-payment__retry">
            <p className="bk-card-payment__help">
              {charged
                ? 'Your booking is saved and the card payment went through. We still need to record it, so please try again.'
                : 'Your booking is saved and your card hasn\'t been charged. Check your card details, then try again.'}
            </p>
            <Button
              className="bk-card-payment__retry-button"
              icon="refresh"
              loading={retrying}
              disabled={retrying}
              onClick={onRetry}
              text="Try payment again"
            />
          </div>
          :
          <p className="bk-card-payment__help">
            {amountLabel ? `Charged ${amountLabel} when you send this booking. ` : 'Charged when you send this booking. '}
            Card details go straight to Stripe.
          </p>
        }
      </div>
    );
  }
}

export default BookingCardPayment;
